import React from "react";
import clsx from "clsx";

export interface DateTimeInputProps {
  /**
   * Current date to display (read-only text). Used when date is not editable.
   */
  currentDateDisplay: string;
  /**
   * Time value in HH:mm format. Empty until both hour and minute are chosen.
   */
  timeValue: string;
  /**
   * Handler for time change. Receives "HH:mm", or "" when a complete time is cleared.
   */
  onTimeChange: (time: string) => void;
  /**
   * Date value in YYYY-MM-DD format for editable date input. When provided, date becomes editable.
   */
  dateValue?: string;
  /**
   * Handler for date change. When provided together with dateValue, date is editable.
   */
  onDateChange?: (date: string) => void;
  /**
   * Input id for form association. Applied to the hour select so the field label focuses it.
   */
  id?: string;
  /**
   * Input name for form association. Hour and minute get distinct names so they
   * do not collide with the hidden datetime field registered by the form.
   */
  name?: string;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Earliest selectable time in HH:mm. Omitted means 00:00.
   */
  minTime?: string;
  /**
   * Latest selectable time in HH:mm, inclusive. Omitted means 23:59.
   */
  maxTime?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
}

interface ClockParts {
  hour: number;
  minute: number;
}

const FULL_DAY_MIN: ClockParts = { hour: 0, minute: 0 };
const FULL_DAY_MAX: ClockParts = { hour: 23, minute: 59 };

const timeSelectClass =
  "min-w-0 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 scheme-light disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400";

/**
 * Parses HH:mm. Returns null for empty or incomplete values.
 */
function parseClock(value?: string): ClockParts | null {
  if (!value) return null;
  const match = /^(\d{2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function resolveBounds(minTime?: string, maxTime?: string): { min: ClockParts; max: ClockParts } {
  const min = parseClock(minTime) ?? FULL_DAY_MIN;
  const max = parseClock(maxTime) ?? FULL_DAY_MAX;
  if (min.hour * 60 + min.minute > max.hour * 60 + max.minute) {
    return { min: FULL_DAY_MIN, max: FULL_DAY_MAX };
  }
  return { min, max };
}

function withExtra(values: number[], extra: number | undefined, upper: number): number[] {
  if (extra === undefined || extra < 0 || extra > upper || values.includes(extra)) {
    return values;
  }
  return [...values, extra].sort((a, b) => a - b);
}

function hoursInRange(min: ClockParts, max: ClockParts, extraHour?: number): number[] {
  const hours: number[] = [];
  for (let hour = min.hour; hour <= max.hour; hour += 1) hours.push(hour);
  return withExtra(hours, extraHour, 23);
}

function minutesInRange(
  hour: number,
  min: ClockParts,
  max: ClockParts,
  extraMinute?: number
): number[] {
  if (hour < min.hour || hour > max.hour) {
    return extraMinute === undefined ? [] : [extraMinute];
  }
  const start = hour === min.hour ? min.minute : 0;
  const end = hour === max.hour ? max.minute : 59;
  if (start > end) return extraMinute === undefined ? [] : [extraMinute];
  const minutes: number[] = [];
  for (let minute = start; minute <= end; minute += 1) minutes.push(minute);
  return withExtra(minutes, extraMinute, 59);
}

/**
 * DateTimeInput
 * Date stays native (read-only text, or type="date" when editable).
 * Time is two native selects. Android Chrome no longer opens the clock dialog
 * for input type="time", and that control does not raise the keyboard either.
 * Selects use the platform list on Android, iOS, and desktop.
 * The parent still receives "HH:mm" inside minTime/maxTime. A value already
 * stored outside that range stays visible so editing does not rewrite it.
 */
export const DateTimeInput: React.FC<DateTimeInputProps> = ({
  currentDateDisplay,
  timeValue,
  onTimeChange,
  dateValue,
  onDateChange,
  id,
  name,
  required,
  minTime,
  maxTime,
  className,
}) => {
  const isDateEditable = dateValue !== undefined && onDateChange !== undefined;
  const { min, max } = resolveBounds(minTime, maxTime);
  const parsed = parseClock(timeValue);

  const [draftHour, setDraftHour] = React.useState("");
  const [draftMinute, setDraftMinute] = React.useState("");

  const hourValue = parsed ? pad2(parsed.hour) : draftHour;
  const minuteValue = parsed ? pad2(parsed.minute) : draftMinute;
  const selectedHour = hourValue === "" ? undefined : Number(hourValue);
  const visibleMinute = minuteValue === "" ? undefined : Number(minuteValue);

  const hourOptions = hoursInRange(min, max, selectedHour);
  const baseMinutes =
    selectedHour === undefined ? [] : minutesInRange(selectedHour, min, max);
  const minuteOptions =
    visibleMinute !== undefined && !baseMinutes.includes(visibleMinute)
      ? [...baseMinutes, visibleMinute].sort((left, right) => left - right)
      : baseMinutes;

  const commit = (nextHour: string, nextMinute: string) => {
    if (nextHour === "") {
      setDraftHour("");
      setDraftMinute("");
      if (parsed) onTimeChange("");
      return;
    }

    const hourNumber = Number(nextHour);
    const allowed = minutesInRange(hourNumber, min, max);
    const minuteNumber = nextMinute === "" ? null : Number(nextMinute);
    const minute =
      minuteNumber !== null && allowed.includes(minuteNumber)
        ? pad2(minuteNumber)
        : allowed.length === 1
          ? pad2(allowed[0])
          : "";
    const hour = pad2(hourNumber);

    setDraftHour(hour);
    setDraftMinute(minute);

    if (minute) {
      const next = `${hour}:${minute}`;
      if (next !== timeValue) onTimeChange(next);
      return;
    }

    if (parsed) onTimeChange("");
  };

  return (
    <div className={clsx("flex flex-col gap-3 sm:flex-row sm:items-center", className)}>
      {isDateEditable ? (
        <input
          type="date"
          value={dateValue}
          onChange={(e) => onDateChange(e.target.value)}
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 scheme-light sm:w-auto sm:min-w-35"
          required={required}
          aria-label="Fecha del evento"
        />
      ) : (
        <span className="text-sm text-gray-600 sm:whitespace-nowrap">{currentDateDisplay}</span>
      )}
      <div
        className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 sm:w-auto sm:grid-cols-[7.5rem_auto_7.5rem]"
        role="group"
        aria-label="Hora y minuto del evento"
      >
        <select
          id={id}
          name={name ? `${name}-hour` : undefined}
          value={hourValue}
          onChange={(event) => commit(event.target.value, minuteValue)}
          required={Boolean(required) && hourValue === ""}
          aria-label="Hora del evento"
          className={timeSelectClass}
        >
          <option value="">Hora</option>
          {hourOptions.map((hour) => (
            <option key={hour} value={pad2(hour)}>
              {pad2(hour)}
            </option>
          ))}
        </select>
        <span className="text-sm text-gray-500" aria-hidden="true">
          :
        </span>
        <select
          id={id ? `${id}-minute` : undefined}
          name={name ? `${name}-minute` : undefined}
          value={minuteOptions.some((minute) => pad2(minute) === minuteValue) ? minuteValue : ""}
          onChange={(event) => commit(hourValue, event.target.value)}
          required={Boolean(required) && hourValue !== "" && minuteValue === ""}
          disabled={hourValue === ""}
          aria-label="Minuto del evento"
          className={timeSelectClass}
        >
          <option value="">Min</option>
          {minuteOptions.map((minute) => (
            <option key={minute} value={pad2(minute)}>
              {pad2(minute)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

DateTimeInput.displayName = "DateTimeInput";

